import { differenceWith, isEqual } from 'lodash-es';
import { useAssignOpoContactsToCase, useRemoveOpoContactsAssignment } from '@/queries/case';
import { useAssignOpoCaseContactsMutation, useUnassignOpoCaseContactsMutation } from '@/queries/opobff';
import { AssignedOpoContactType, OpoContactIdIsPrimaryPair, OpoContactWithPrimary } from '@/api/apis/case';

const normalizePhone = (phone?: string | null) => phone || undefined;

const hasContactId = (contact: OpoContactWithPrimary): contact is OpoContactWithPrimary => {
  return Boolean(contact?.opoContactId);
};

const normalizeContacts = (contacts: OpoContactWithPrimary[]): OpoContactWithPrimary[] => {
  return contacts.filter(hasContactId).map((contact) => ({
    ...contact,
    primaryPhone: normalizePhone(contact.primaryPhone),
  }));
};

const getContactById = (contacts: OpoContactWithPrimary[], contactId: string) => {
  return contacts.find((contact) => contact.opoContactId === contactId);
};

export const useUpdateOpoContacts = (isOpoCase = false) => {
  const { mutateAsync: assignTxCase } = useAssignOpoContactsToCase();
  const { mutateAsync: detachTxCase } = useRemoveOpoContactsAssignment();
  const { mutateAsync: assignOpoCase } = useAssignOpoCaseContactsMutation();
  const { mutateAsync: detachOpoCase } = useUnassignOpoCaseContactsMutation();

  const runMutations = async (
    existingIds: OpoContactWithPrimary[],
    newIds: OpoContactWithPrimary[],
    caseId: string,
  ) => {
    const existingValidIds = normalizeContacts(existingIds);
    const newValidIds = normalizeContacts(newIds);

    const contactsToAttach = newValidIds.filter((newContact) => {
      const existingContact = getContactById(existingValidIds, newContact.opoContactId);

      return !existingContact || !isEqual(existingContact, newContact);
    });

    const contactIdsToDetach = differenceWith(
      existingValidIds.map((contact) => contact.opoContactId),
      newValidIds.map((contact) => contact.opoContactId),
      isEqual,
    );

    let mutated = false;

    if (contactsToAttach.length) {
      mutated = true;
      if (isOpoCase) {
        await assignOpoCase({
          caseId,
          opoContactIdIsPrimaryPairs: contactsToAttach.map((c) => ({ ...c, isPrimary: c.isPrimary ?? false })),
        });
      } else {
        await assignTxCase({ caseId, contacts: contactsToAttach });
      }
    }

    if (contactIdsToDetach.length) {
      mutated = true;
      if (isOpoCase) {
        await detachOpoCase({ caseId, opoContactIds: contactIdsToDetach });
      } else {
        await detachTxCase({ caseId, contactIds: contactIdsToDetach });
      }
    }

    return mutated;
  };

  return runMutations;
};

export const mapAssignedOPOContacts = (assignedOpoContacts: AssignedOpoContactType[]): OpoContactIdIsPrimaryPair[] =>
  assignedOpoContacts.map((contact) => ({
    opoContactId: contact.id,
    isPrimary: contact.isPrimary,
    primaryPhone: normalizePhone(contact.primaryPhone),
  }));

export const getOPOFormContacts = (
  primaryContactId?: string | null,
  secondaryContactId?: string | null,
  primaryContactPhone?: string | null,
  secondaryContactPhone?: string | null,
): OpoContactIdIsPrimaryPair[] => {
  const opoContacts: OpoContactIdIsPrimaryPair[] = [];

  if (primaryContactId) {
    opoContacts.push({
      opoContactId: primaryContactId,
      isPrimary: true,
      primaryPhone: normalizePhone(primaryContactPhone),
    });
  }

  if (secondaryContactId && secondaryContactId !== primaryContactId) {
    opoContacts.push({
      opoContactId: secondaryContactId,
      isPrimary: !primaryContactId,
      primaryPhone: normalizePhone(secondaryContactPhone),
    });
  }

  return opoContacts;
};

type OPOFields = {
  opoContactId?: string;
  opoSourceContactPhone?: string;
  secondaryOpoContactId?: string;
  secondaryOpoContactPhone?: string;
};

export const getOPOContactsToMutate = (
  shouldMutateOPOContacts: boolean,
  assignedOpoContacts: AssignedOpoContactType[],
  { opoContactId, opoSourceContactPhone, secondaryOpoContactId, secondaryOpoContactPhone }: OPOFields,
): {
  currentValue: OpoContactIdIsPrimaryPair[];
  newValue: OpoContactIdIsPrimaryPair[];
} => {
  if (!shouldMutateOPOContacts) {
    return {
      currentValue: mapAssignedOPOContacts(assignedOpoContacts),
      newValue: [],
    };
  }

  return {
    currentValue: mapAssignedOPOContacts(assignedOpoContacts),
    newValue: getOPOFormContacts(opoContactId, secondaryOpoContactId, opoSourceContactPhone, secondaryOpoContactPhone),
  };
};
